import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { resolveApiAssetUrl } from '../lib/api';
import { HOOKS } from '../lib/domHooks';
import { looksLikeHtml } from '../lib/richHtmlFrame';
import {
  isInternalLink, markWelcomeSeen, readWelcomeSeen, shouldShowWelcomePopup,
} from '../lib/welcomePopup';
import './WelcomePopup.css';

/**
 * Pencerenin görünümü. Panelin önizlemesi de bunu çizer — sitede ve panelde
 * aynı pencere görünmelidir.
 *
 * HTML metin süzülmeden portalın DOM'una basılır (`dangerouslySetInnerHTML`).
 * Bir süre sandbox iframe'inde çiziliyordu: özel CSS `.welcome-popup-body`
 * içine ulaşamıyor, çerçeve ek DOM ve ölçü gecikmesi üretiyordu. İçeriği
 * yalnızca `content.write` izinli yönetici yazar (bkz.
 * backend/services/welcome_popup.py).
 */
export const WelcomePopupCard = ({ popup, onClose, onAction }) => {
  const image = popup.image_url ? resolveApiAssetUrl(popup.image_url) : '';
  const hasButton = Boolean(popup.button_label && popup.button_url);
  const internal = isInternalLink(popup.button_url);
  const hasContent = Boolean(popup.title || popup.body || hasButton);

  const renderLink = (children, className, extra = {}) => {
    if (!hasButton) return null;
    if (internal) {
      return (
        <Link to={popup.button_url} className={className} onClick={onAction} {...extra}>
          {children}
        </Link>
      );
    }
    return (
      <a href={popup.button_url} target="_blank" rel="noopener noreferrer"
        className={className} onClick={onAction} {...extra}>
        {children}
      </a>
    );
  };

  const media = image ? (
    <img className="welcome-popup-image" src={image} alt={popup.title || ''} />
  ) : null;

  return (
    <div className="welcome-popup-dialog" onClick={(event) => event.stopPropagation()}>
      {onClose && (
        <button type="button" className="welcome-popup-close" onClick={onClose} aria-label="Kapat">
          <X size={18} />
        </button>
      )}
      {/* Kaydırma **iç** katmandadır, pencerenin kendisinde değil: kapatma
          düğmesi pencereye göre konumlanır ve içerik uzun olduğunda
          (telefonda uzun bir görsel) onunla birlikte yukarı kaymaz. Düğme
          kaydırılan katmanın içindeyken dar ekranda erişilemez oluyordu. */}
      <div className="welcome-popup-scroll">
        {media && (hasButton
          ? renderLink(media, 'welcome-popup-media', { 'aria-label': popup.button_label })
          : <div className="welcome-popup-media">{media}</div>)}
        {hasContent && (
          <div className="welcome-popup-content">
            {popup.title && (
              <h2 id="welcome-popup-title" className="welcome-popup-title">{popup.title}</h2>
            )}
            {popup.body && (
              looksLikeHtml(popup.body)
                ? <div className="welcome-popup-body" dangerouslySetInnerHTML={{ __html: popup.body }} />
                : <div className="welcome-popup-body welcome-popup-body-text">{popup.body}</div>
            )}
            {renderLink(popup.button_label, 'welcome-popup-button gold-btn')}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Siteye girene gösterilen karşılama penceresi (panel: Görünüm & Ayarlar →
 * Karşılama Penceresi).
 *
 * Oturum durumu bilinmeden karar verilmez: jetonu olan üye sayfayı
 * yenilediğinde `/auth/me` gelene kadar `user` boştur ve "yalnızca
 * ziyaretçilere" penceresi üyeye bir an görünürdü. Sayfa açıkken bir kez
 * gösterilir; kapatıldıktan sonra rota değişse de yeniden açılmaz.
 */
const WelcomePopup = () => {
  const settings = useSettings();
  const { user, ready } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const handled = useRef(0);
  const popup = settings.welcome_popup || null;
  const version = Number(popup?.version) || 0;

  useEffect(() => {
    if (!ready || !popup || settings.site_locked) return undefined;
    if (handled.current === version) return undefined;
    const decision = shouldShowWelcomePopup(popup, {
      user, pathname, ...readWelcomeSeen(),
    });
    if (!decision) return undefined;
    const delay = Math.max(0, Number(popup.delay_seconds) || 0) * 1000;
    const timer = window.setTimeout(() => {
      handled.current = version;
      markWelcomeSeen(popup);
      setOpen(true);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [popup, version, ready, user, pathname, settings.site_locked]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Pencere açıkken panelden kapatılırsa (ayarlar yenilenir) hemen kalkar.
  if (!open || !popup || settings.site_locked) return null;

  return (
    <div
      className="welcome-popup-root"
      data-bhs={HOOKS.WELCOME_POPUP}
      role="dialog"
      aria-modal="true"
      aria-labelledby={popup.title ? 'welcome-popup-title' : undefined}
      aria-label={popup.title ? undefined : 'Duyuru'}
      onClick={close}
    >
      <div className="welcome-popup-backdrop" />
      <WelcomePopupCard popup={popup} onClose={close} onAction={close} />
    </div>
  );
};

export default WelcomePopup;
